import axios from 'axios';
import { getBaseUrl } from './helpers';

// API Endpoints
const LOGIN_ENDPOINT = `/auth/api/user/login/`;
const FORGOT_PASSWORD_ENDPOINT = `/auth/api/user/password-reset/`;
const RESET_PASSWORD_ENDPOINT = `/auth/api/user/password-reset-confirm/`;
const CHANGE_PASSWORD_ENDPOINT = `/auth/api/user/change-password/`;
const UPDATE_PROFILE_ENDPOINT = `/auth/api/user/update/`;
const VERIFY_BUILDING_CODE_ENDPOINT = `/auth/api/tokens/registration-code/`;
const REGISTER_BUILDING_USER_ENDPOINT = `/auth/api/building-users/activate-code/`;
const VALIDATE_VERSION_ENDPOINT = `/auth/api/versions/validate/`;
const CREATE_VERSION_ENDPOINT = `/auth/api/versions/`;
const USER_ENDPOINT = `/auth/api/user/`;
const REFRESH_TOKEN_ENDPOINT = `/auth/api/user/refresh-token/`;

export const logout = () => {
  localStorage.removeItem('current_user');
  localStorage.removeItem('refresh_token');
  sessionStorage.removeItem('token');
};

export const refreshToken = (env) => {
  const BASE_URL = getBaseUrl(env);

  let token;

  try {
    token = JSON.parse(localStorage.getItem('refresh_token'));
  } catch (error) {
    return "Could not authenticate.";
  }

  const response = axios
    .post(`${BASE_URL}${REFRESH_TOKEN_ENDPOINT}`, {
      token,
    }, {
      headers: {
        'User-Agent': 'web',
      }
    })
    .then((response) => {
      const { data } = response;
      // const data = response.data;

      if (data && data.token && data.refresh_token) {
        // handle storing user details and jwt token in local storage to keep user logged in
        localStorage.setItem('current_user', JSON.stringify(data.user))
        localStorage.setItem('refresh_token', JSON.stringify(data.refresh_token));
        sessionStorage.setItem('token', JSON.stringify(data.token));
        return data;
      }

      return response.status;
    })
    .catch((error) => {
      logout();
      return error.response.status;
    });

  return response;
}

export const login = async (env, email, password) => {
  const BASE_URL = getBaseUrl(env);

  const response = await axios
    .post(`${BASE_URL}${LOGIN_ENDPOINT}`, {
      email,
      password,
    }, {
      headers: {
        'User-Agent': 'web',
      }
    })
    .then((response) => {
      const user = response.data;
      // login is successful if there's a jwt token in the response
      if (user && user.token && user.refresh_token) {
        // handle storing user details and jwt token in local storage to keep user logged in
        localStorage.setItem('current_user', JSON.stringify(user.user));
        localStorage.setItem('refresh_token', JSON.stringify(user.refresh_token));
        sessionStorage.setItem('token', JSON.stringify(user.token));
        return user;
      }

      return response;
    }, (error) => {
      sessionStorage.removeItem('token');
      return error.response.data.errors[ 0 ].message;
    });

  return response;
};


export const forgotPassword = async (env, email, url) => {
  const BASE_URL = getBaseUrl(env);

  const response = await axios
    .post(`${BASE_URL}${FORGOT_PASSWORD_ENDPOINT}`, {
      email,
      url
    })
    // eslint-disable-next-line no-unused-vars
    .then((response) => {
      return true;
    }, (error) => {
      return error.response.data.errors[ 0 ].message;
    });

  return response;
};

export const resetPassword = async (env, url, newPassword1, newPassword2, userHash, token) => {
  const BASE_URL = getBaseUrl(env);

  const response = await axios
    .post(`${BASE_URL}${RESET_PASSWORD_ENDPOINT}`, {
      url,
      new_password1: newPassword1,
      new_password2: newPassword2,
      user_hash: userHash,
      token,
    })
    // eslint-disable-next-line no-unused-vars
    .then((response) => {
      return true;
    })
    .catch((error) => {
      return error.response.data.errors[ 0 ].message;
    });

  return response;
};

export const changePassword = (env, oldPassword, newPassword1, newPassword2) => {
  const BASE_URL = getBaseUrl(env);
  let authToken = JSON.parse(sessionStorage.getItem('token')) || '';

  if (!authToken) {
    refreshToken(env);

    authToken = JSON.parse(sessionStorage.getItem('token')) || '';
  }

  const response = axios
    .post(`${BASE_URL}${CHANGE_PASSWORD_ENDPOINT}`, {
      old_password: oldPassword,
      new_password1: newPassword1,
      new_password2: newPassword2,
    }, {
      headers: {
        'Authorization': `JWT ${authToken}`,
        'Content-Type': 'application/json',
      },
    })
    .then((response) => {
      return response.status;
    })
    .catch((error) => {
      if (error.response.status === 401) {
        refreshToken(env);

        return changePassword(env, oldPassword, newPassword1, newPassword2);
      }
      return Promise.reject(error.response.data.errors[ 0 ].message);
    });

  return response;
};

export const updateProfile = (env, email, first_name, last_name, phone_number) => {
  const BASE_URL = getBaseUrl(env);
  let authToken = JSON.parse(sessionStorage.getItem('token')) || '';

  if (!authToken) {
    refreshToken(env);

    authToken = JSON.parse(sessionStorage.getItem('token')) || '';
  }

  const response = axios
    .patch(`${BASE_URL}${UPDATE_PROFILE_ENDPOINT}`, {
      email,
      first_name,
      last_name,
      phone_number,
    }, {
      headers: {
        'Authorization': `JWT ${authToken}`,
        'Content-Type': 'application/json',
      },
    })
    .then((response) => {
      const user = response.data;
      localStorage.setItem('current_user', JSON.stringify(user));
      return response.status;
    })
    .catch((error) => {
      if (error.response.status === 401) {
        refreshToken(env);

        return updateProfile(env, email, first_name, last_name, phone_number);
      }
      return Promise.reject(error.response.data.errors[ 0 ].message);
    });

  return response;
};

export const registerUser = (env, email, first_name, last_name, phone_number = '', password) => {
  const BASE_URL = getBaseUrl(env);

  const response = axios
    .post(`${BASE_URL}${USER_ENDPOINT}`, {
      email,
      first_name,
      last_name,
      phone_number,
      password,
    })
    .then((response) => {
      return response.status;
    }, (error) => {
      return error.response.data.errors[ 0 ].message;
    });

  return response;
};

export const verifyBuildingCode = (env, code) => {
  const BASE_URL = getBaseUrl(env);
  let authToken = JSON.parse(sessionStorage.getItem('token')) || '';

  if (!authToken) {
    refreshToken(env);

    authToken = JSON.parse(sessionStorage.getItem('token')) || '';
  }

  const response = axios
    .get(`${BASE_URL}${VERIFY_BUILDING_CODE_ENDPOINT}${code}/`, {
      headers: {
        'Authorization': `JWT ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      if (error.response.status === 401) {
        refreshToken(env);

        return verifyBuildingCode(env, code);
      }
      return Promise.reject(error.response.data.errors[ 0 ].message);
    });

  return response;
};

export const registerBuildingUser = (env, registration_code, user_role, is_receiving_alerts) => {
  const BASE_URL = getBaseUrl(env);
  let authToken = JSON.parse(sessionStorage.getItem('token')) || '';

  if (!authToken) {
    refreshToken(env);

    authToken = JSON.parse(sessionStorage.getItem('token')) || '';
  }

  const response = axios
    .post(`${BASE_URL}${REGISTER_BUILDING_USER_ENDPOINT}`, {
      registration_code,
      user_role,
      is_receiving_alerts,
    }, {
      headers: {
        'Authorization': `JWT ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then((response) => {
      return response.status;
    })
    .catch((error) => {
      if (error.response.status === 401) {
        refreshToken(env);

        return registerBuildingUser(env, registration_code, user_role, is_receiving_alerts);
      }
      return Promise.reject(error.response.data.errors[ 0 ].message);
    });

  return response;
};

export const validateVersion = (env, version, id) => {
  const BASE_URL = getBaseUrl(env);
  let authToken = JSON.parse(sessionStorage.getItem('token')) || '';

  if (!authToken) {
    refreshToken(env);

    authToken = JSON.parse(sessionStorage.getItem('token')) || '';
  }

  const response = axios
    .get(`${BASE_URL}${VALIDATE_VERSION_ENDPOINT}${id}?version=${version}`, {
      headers: {
        'Authorization': `JWT ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      if (error.response.status === 401) {
        refreshToken(env);

        return validateVersion(env, version, id);
      }
      return Promise.reject(error.response.data.errors[ 0 ].message);
    });

  return response;
};

export const createVersion = (env, is_latest, version, app) => {
  const BASE_URL = getBaseUrl(env);
  let authToken = JSON.parse(sessionStorage.getItem('token')) || '';

  if (!authToken) {
    refreshToken(env);

    authToken = JSON.parse(sessionStorage.getItem('token')) || '';
  }

  const response = axios
    .post(`${BASE_URL}${CREATE_VERSION_ENDPOINT}`, {
      is_latest,
      app,
      version,
    }, {
      headers: {
        'Authorization': `JWT ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      if (error.response.status === 401) {
        refreshToken(env);

        return createVersion(env, is_latest, version, app);
      }
      return Promise.reject(error.response.data.errors[ 0 ].message);
    });

  return response;
};
